import { useAppDispatch, useAppSelector } from 'hooks/redux';
// CORE
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { useToggle } from 'react-use';

// HOOKS
import useTextFieldState from 'hooks/useTextFieldState';

import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
// MUI
import QButton from 'components/QButton';
import Checkbox from '@mui/material/Checkbox';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

// APP-COMPONENTS
import { actionHelpers } from 'app-components/flux/core';
import { preferencesActions, preferencesSelectors } from 'app-components/flux/preferences';
import { profileActions, profileSelectors } from 'app-components/flux/profile';
import { rootUiActions } from 'app-components/flux/root-ui';
import { loadState, useLoadState } from 'app-components/hooks/utils';
import { crashReporterInterface, tracker } from 'app-components/utils/core';

// Shared
import { showOnboardAccountsDialog } from 'components/Dialogs/A2A/actions';
import { onboardA2ADialogIndexEnums } from 'components/Dialogs/A2A/onboardA2ADialogIndexEnums';
import StdDialog from 'components/Dialogs/StdDialog';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import LoadingView from 'components/LoadingView';
import DateField from 'components/Transactions/DateField';
import ZeroStateView from 'components/ZeroStateView';

import QIllustration from 'components/QIllustration';
// LOCAL
import SSNField from './SSNField';
import SecurityPrivacyTrust from './SecurityPrivacyTrust';
import SetupPage from './SetupPage';
import { identityDialogIndexEnums } from './identityDialogIndexEnums';
import useStyles from './styles';

export const DIALOG_TYPE_IDENTITY_VERIFICATION_DIALOG = 'DIALOG_TYPE_IDENTITY_VERIFICATION_DIALOG';

const ALL_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

// Error if the address contains any of these strings after being normalized to remove spaces, punctuation, and capitalization
const INVALID_ADDRESS_SUBSTRINGS = ['pobox', 'postofficebox', 'pbøx', 'pøbøx', 'pøbox', 'pobøx'];

// Error if the address contains any of these strings as their own independent word normalized to remove punctuation and capitalization
const INVALID_ADDRESS_STRINGS = ['pbox', 'pob', 'pøb'];

/*  VIEW INDEXES MAP
  READY : For when selector data is ready to display
  ERROR : For when there was an error with a request
  FAILED : For when there was a failure retrieving selector data
  LOADING : For when selector data is loading
*/
function IdentityVerificationDialog(props) {
  const isMounted = useRef();
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const { initiator, startTabIndex } = props;

  // =============== SELECTORS =================
  const preferences = useAppSelector(preferencesSelectors.getPreferences);
  const profile = useAppSelector(profileSelectors.getProfile);
  const isActiveIdentityValid = useAppSelector(profileSelectors.getIdentityVerified);
  const emailVerified = useAppSelector(profileSelectors.getEmailVerified);
  const phoneVerified = useAppSelector(profileSelectors.getPhoneVerified);
  const verificationStatus = useAppSelector(profileSelectors.getIdentityStatus);
  const verificationAttemptsAvailable = useAppSelector(profileSelectors.getVerificationAttemptsAvailable);
  const incompleteVerificationAttemptsAvailable = useAppSelector(
    profileSelectors.getVerificationAttemptedButIncomplete,
  );

  // ================= VARIABLES ====================
  const sharedUserPreferences = preferences && preferences.shared.user;
  const maxAttemptsReached = !verificationAttemptsAvailable;

  let initialIndex = startTabIndex || identityDialogIndexEnums.OPENING;
  if (
    (verificationStatus === 'FAILED' || incompleteVerificationAttemptsAvailable) &&
    verificationAttemptsAvailable &&
    !isActiveIdentityValid
  ) {
    initialIndex = identityDialogIndexEnums.SUBMIT;
  } else if (verificationStatus === 'FAILED' && !verificationAttemptsAvailable && !isActiveIdentityValid) {
    initialIndex = identityDialogIndexEnums.LOCKOUT;
  } else if (verificationStatus === 'PENDING' && !isActiveIdentityValid) {
    initialIndex = identityDialogIndexEnums.PENDING;
  } else if (isActiveIdentityValid || verificationStatus === 'PASSED') {
    initialIndex = identityDialogIndexEnums.SUCCESS;
  }

  // ==================== STATES ===================
  const [tabIndex, setTabIndex] = useState(initialIndex);

  // Fields
  const [firstName, setFirstName] = useTextFieldState('');
  const [lastName, setLastName] = useTextFieldState('');
  const [streetAddress, setStreetAddress] = useTextFieldState('');
  const [aptSuite, setAptSuite] = useTextFieldState('');
  const [city, setCity] = useTextFieldState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useTextFieldState('');
  const [ssn, setSsn] = useState('');
  const [dob, setDob] = useState();

  const [isCurrentFailedAttempt, toggleIsCurrentFailedAttempt] = useToggle(false);
  const [agreeToTerms, toggleAgreeToTerms] = useToggle(verificationStatus === 'FAILED');
  const [touchedFields, setTouchedFields] = useState({}); // tracks which fields have been focused and then blurred
  const [errorCode, setErrorCode] = useState(null);
  const [errorMessage, setErrorMessage] = useState('Please try again at another time.');

  //  ================= VIEW STATE LOGIC =====================
  //  Views
  const profileView = useLoadState(profileSelectors);

  const mainView = useMemo(() => {
    /*  tabIndex > useLoadState */
    //  View states
    const showErrorView =
      tabIndex === identityDialogIndexEnums.ERROR ||
      tabIndex === identityDialogIndexEnums.LOCKOUT ||
      !emailVerified ||
      !phoneVerified;
    const showSkeletonView =
      loadState.skeletonStates.includes(profileView) || tabIndex === identityDialogIndexEnums.LOADING;
    const showTryAgainView =
      (loadState.tryAgainStates.includes(profileView) || loadState.updateRequiredStates.includes(profileView)) &&
      tabIndex !== identityDialogIndexEnums.LOADING;
    const showNoDataView =
      loadState.noDataStates.includes(profileView) && tabIndex !== identityDialogIndexEnums.LOADING;
    const showReadyView = loadState.readyStates.includes(profileView) && tabIndex !== identityDialogIndexEnums.LOADING;

    //  Ordered by priority
    let myMainView = '';
    if (showErrorView) {
      myMainView = 'ERROR';
    } else if (showReadyView || showNoDataView) {
      //  There will be no EMPTY view for this dialog
      myMainView = 'READY';
    } else if (showTryAgainView) {
      myMainView = 'FAILED';
    } else if (showSkeletonView) {
      myMainView = 'LOADING';
    }

    // MainView overrides
    if (!emailVerified || !phoneVerified) {
      myMainView = 'NO_EMAIL';
    }
    return myMainView;
  }, [emailVerified, phoneVerified, profileView, tabIndex]);

  const containsPOBoxString = () => {
    // Remove all punctuation, spaces, and makes lowercase.
    const normalizedAddress = streetAddress.replace(/[\s\W_]+/g, '').toLowerCase();
    // Error if the address contains any of these strings after being normalized to remove spaces, punctuation, and capitalization
    const hasInvalidSubstr = INVALID_ADDRESS_SUBSTRINGS.some((invalidString) =>
      normalizedAddress.includes(invalidString),
    );

    // Remove all punctuation and make lowercase.
    const normalizedAddressWithSpaces = streetAddress.replace(/[^\w\s]|_/g, '').toLowerCase();
    // Error if the address contains any of these strings as their own independent word normalized to remove punctuation and capitalization
    const hasInvalidStr = INVALID_ADDRESS_STRINGS.some((invalidString) => {
      const regex = new RegExp(`\\b${invalidString}\\b`, 'i'); // Match as whole word
      return regex.test(normalizedAddressWithSpaces);
    });

    return hasInvalidSubstr || hasInvalidStr;
  };

  const getFieldErrors = (onlyIfTouched = false) => {
    // onlyIfTouched allows us to check all fields regardless of touch on submit
    let retErrors = {};

    if (dob && (!onlyIfTouched || touchedFields.dob)) {
      if (!Date.parse(dob) || !dob?.toISODate()) {
        retErrors.dob = 'Invalid date format';
      } else if (Math.abs(dob.diff(DateTime.local(), 'years').years) < 18) {
        retErrors.dob = 'You must be 18 years or older';
      } else if (Math.abs(dob.diff(DateTime.local(), 'years').years) >= 125) {
        retErrors.dob = 'Cannot be older than 125';
      }
    }
    if (
      streetAddress &&
      (!onlyIfTouched || touchedFields.streetAddress) &&
      streetAddress.length > 0 &&
      containsPOBoxString()
    ) {
      retErrors.streetAddress = 'PO Boxes are not allowed. Please enter your home address.';
    }
    if (zipCode && (!onlyIfTouched || touchedFields.zipCode) && zipCode.length < 5) {
      retErrors.zipCode = 'Enter a valid 5-digit ZIP Code';
    }
    if (state && (!onlyIfTouched || touchedFields.state) && !ALL_STATES.includes(state)) {
      retErrors.state = 'Invalid state';
    }
    if ((!onlyIfTouched || touchedFields.ssn) && (!ssn || ssn?.length === 0)) {
      retErrors.ssn = 'Your SSN is required';
    } else if ((!onlyIfTouched || touchedFields.ssn) && ssn?.length > 0 && ssn?.length !== 4) {
      retErrors.ssn = 'Your SSN must be 4 digits long';
    }
    if (Object.keys(retErrors).length === 0) {
      retErrors = null;
    }
    return retErrors;
  };
  const fieldErrors = getFieldErrors(true);

  // ==================== CALLBACK FUNCTIONS =====================
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const refreshData = useCallback(() => {
    dispatch(profileActions.getProfile());
  }, [dispatch]);

  const handleClose = (_event, reason) => {
    let pageAction = '';
    pageAction = reason === 'CANCEL' ? 'cancel' : pageAction;
    pageAction = reason === 'CLOSE_BUTTON' ? 'select x to close window' : pageAction;
    if (tabIndex === identityDialogIndexEnums.SUBMIT) {
      // closing before submitting
      tracker.track(tracker.events.identityFormCancel, {
        first_name: firstName?.length > 0 ? 'completed' : 'blank',
        last_name: lastName?.length > 0 ? 'completed' : 'blank',
        street_address1: streetAddress?.length > 0 ? 'completed' : 'blank',
        street_address2: aptSuite?.length > 0 ? 'completed' : 'blank',
        city: city?.length > 0 ? 'completed' : 'blank',
        state: state?.length > 0 ? 'completed' : 'blank',
        zip_code: zipCode?.length > 0 ? 'completed' : 'blank',
        date_of_birth: dob?.length > 0 ? 'completed' : 'blank',
        ssn: ssn?.length > 0 ? 'completed' : 'blank',
        disclosures_checkbox: agreeToTerms ? 'completed' : 'blank',
      });
    } else if (tabIndex === identityDialogIndexEnums.LOCKOUT) {
      tracker.track(tracker.events.identityUnableToComplete, { page_action: pageAction }); //  TODO: Add 'notify me' to this list of options when available
    } else if (tabIndex === identityDialogIndexEnums.SUCCESS) {
      pageAction = reason === 'CANCEL' ? 'finish later' : pageAction;
      tracker.track(tracker.events.identityVerificationCompleted, { page_action: pageAction });
    }
    dispatch(rootUiActions.removeDialog());
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handleTouchField = useCallback(
    (e) => {
      const fieldName = e.target.id.replace('-field', ''); // extract the name of the field
      setTouchedFields({ ...touchedFields, [fieldName]: true });
    },
    [touchedFields],
  );

  const handleErrors = (error) => {
    if (isMounted.current === true) {
      setErrorCode(error?.response?.status);
      setErrorMessage(error.response?.data?.errors[0]?.detail || error.response?.data?.errors[0]?.title);
      setTabIndex(identityDialogIndexEnums.ERROR);
      tracker.track(tracker.events.identityVerificationError, { error_code: error?.response?.status });
    }
    crashReporterInterface.reportError(error);
  };

  // There are two dashboard cards for A2A, the "View Identity" card, and the "Set Up" card. The view identity card should only appear
  // when we have gone from a PENDING state to any confirmation state "Failure, success, etc".
  // This simple function just easily changes the preference based on the setting the user enters (true/false)
  // There is also an if statement that only changes it if it is changing to a NEW value just to avoid pointless preferenceActions.
  const setIdentityViewCardPreference = useCallback(
    (setting) => {
      if (sharedUserPreferences?.a2a?.showIdentityReviewedCard !== setting) {
        dispatch(
          preferencesActions.setPreference({
            section: 'shared',
            group: 'user',
            preference: { a2a: { showIdentityReviewedCard: setting } },
          }),
        );
      }
    },
    [dispatch, sharedUserPreferences],
  );

  const sendVerificationPackage = async () => {
    //  dispatch(profileActions.getProfile(undefined)); // last second sanity check for the current state of the profile
    if (getFieldErrors(false)) return;
    tracker.track(tracker.events.identityFormSubmitted);
    setTabIndex(identityDialogIndexEnums.LOADING);

    const email = profile?.get('primaryEmail')?.get('address');
    const phone = profile?.get('primaryPhone')?.get('number');

    const addressPayload = {
      firstName,
      lastName,
      dateOfBirth: dob?.toISODate(),
      email,
      socialSecurityNumber: ssn,
      telephone: phone.substr(phone.length - 10, 10),
      street: streetAddress,
      addressLine2: aptSuite,
      city,
      state,
      zip: zipCode,
    };
    if (emailVerified && phoneVerified) {
      // last second sanity check for verification
      //  dispatch(profileActions.verifyIdentity(addressPayload));

      actionHelpers
        .bindPromiseAction(
          dispatch,
          profileActions.verifyIdentity,
        )(addressPayload)
        .then((resp) => {
          if (isMounted.current === true) {
            dispatch(profileActions.getProfile());
            tracker.track(tracker.events.identityVerificationResult, { verification_status: resp?.data?.status });
            const maxCallsMade = resp?.data?.verificationThreshold <= resp?.data?.callCount;
            if (
              (resp?.data?.status === 'FAILED' || resp?.data?.status === 'UNKNOWN' || !verificationStatus) &&
              !maxCallsMade
            ) {
              toggleIsCurrentFailedAttempt();
              tracker.track(tracker.events.identityVerificationSubmitted, { status: 'soft failure' });
              setIdentityViewCardPreference(false);
              setSsn('');
              setTabIndex(identityDialogIndexEnums.SUBMIT);
            } else if (
              (resp?.data?.status === 'FAILED' || resp?.data?.status === 'UNKNOWN' || !verificationStatus) &&
              maxCallsMade
            ) {
              tracker.track(tracker.events.identityVerificationSubmitted, { status: 'hard failure' });
              setIdentityViewCardPreference(false);
              setTabIndex(identityDialogIndexEnums.LOCKOUT);
            } else if (resp?.data?.status === 'PENDING') {
              tracker.track(tracker.events.identityVerificationSubmitted, { status: 'pending' });
            } else if (resp?.data?.status === 'PASSED') {
              tracker.track(tracker.events.identityVerificationSubmitted, { status: 'pass' });
            }
          }
        })
        .catch((error) => {
          // TODO: Fix this bug with Chris in a separate PR due to complexity
          if (maxAttemptsReached) {
            if (isMounted.current === true) {
              dispatch(profileActions.getProfile());
              setIdentityViewCardPreference(false);
              setTabIndex(identityDialogIndexEnums.LOCKOUT);
            }
          } else {
            handleErrors(error);
          }
        });
    }
  };

  const handleContinue = (e) => {
    if (tabIndex === identityDialogIndexEnums.OPENING) {
      setTabIndex(identityDialogIndexEnums.SUBMIT);
    } else if (tabIndex === identityDialogIndexEnums.SUBMIT) {
      sendVerificationPackage();
    } else if (tabIndex === identityDialogIndexEnums.PENDING) {
      handleClose(e, 'CANCEL');
    } else {
      //  tracker.track(tracker.events.identityVerificationCompleted, { page_action: 'continue' });
      handleClose(e, 'COMPLETE');
      if (tabIndex === identityDialogIndexEnums.SUCCESS) {
        dispatch(
          showOnboardAccountsDialog({
            initiator: 'identity verification',
            startTabIndex: onboardA2ADialogIndexEnums.SELECT_INST,
            overrideOpening: true,
          }),
        );
      }
    }

    setTouchedFields({}); // reset touched fields
  };

  const handleOpenTerms = (e) => {
    e.stopPropagation(); // Prevent event from bubbling up to the checkbox
    e.preventDefault(); // Prevent default behavior
    tracker.track(tracker.events.identitySelectDisclosure);
    window.open('https://www.quicken.com/simplifi/terms', '_blank');

    // might be used later for now doing above ^^
    // const newTab = window.open('', '_blank');
    // const terms = 'TEMPORARY TERMS OF SERVICE COPY';
    // newTab.document.write(`<p>${terms}</p>`);
  };

  const handleOpenPrivacyPolicy = () => {
    window.open('https://www.quicken.com/privacy/us', '_blank');
  };
  const handleOpenDwollaTerms = () => {
    window.open('https://www.dwolla.com/legal/tos/', '_blank');
  };
  const handleOpenDwollaPrivacyPolicy = () => {
    window.open('https://www.dwolla.com/legal/privacy/', '_blank');
  };

  const defaultCharLimit = 50;

  const zipChange = (e) => {
    const val = e.target.value;
    if (val.toString().length > 5) return;
    setZipCode(e);
  };

  const firstNameChange = (e) => {
    const val = e.target.value;
    if (val.toString().length > defaultCharLimit) return;
    setFirstName(e);
  };

  const lastNameChange = (e) => {
    const val = e.target.value;
    if (val.toString().length > defaultCharLimit) return;
    setLastName(e);
  };

  const cityChange = (e) => {
    const val = e.target.value;
    if (val.toString().length > defaultCharLimit) return;
    setCity(e);
  };

  const addressChange = (e) => {
    const val = e.target.value;
    if (val.toString().length > defaultCharLimit) return;
    setStreetAddress(e);
  };

  const aptSuiteChange = (e) => {
    const val = e.target.value;
    if (val.toString().length > defaultCharLimit) return;
    setAptSuite(e);
  };

  // =================== EFFECTS ===================

  // track mounted state because of async promise
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Get the initial verification status!
  useEffect(() => {
    dispatch(profileActions.getProfile());
    dispatch(profileActions.getVerificationStatus());
  }, [dispatch]);

  useEffect(() => {
    if (verificationStatus) {
      if (isActiveIdentityValid) {
        // User entered correct info and the identity is valid
        setIdentityViewCardPreference(false);
        setTabIndex(identityDialogIndexEnums.SUCCESS);
      } else if (verificationStatus === 'FAILED' && !maxAttemptsReached) {
        // User entered bad info at first
        setIdentityViewCardPreference(false);
        setSsn('');
        setTabIndex(identityDialogIndexEnums.SUBMIT);
      } else if (maxAttemptsReached) {
        // User entered bad info too many times
        setIdentityViewCardPreference(false);
        setTabIndex(identityDialogIndexEnums.LOCKOUT);
      } else if (verificationStatus === 'PENDING') {
        // We are still checking their previously submitted data!
        setIdentityViewCardPreference(true);
        setTabIndex(identityDialogIndexEnums.PENDING);
      }
    }
  }, [isActiveIdentityValid, verificationStatus, maxAttemptsReached, setIdentityViewCardPreference]);

  useEffect(() => {
    if (tabIndex === identityDialogIndexEnums.OPENING) {
      tracker.track(tracker.events.viewTransfersIntroScreen, { initiator: 'identity' });
    } else if (tabIndex === identityDialogIndexEnums.SUBMIT) {
      tracker.track(tracker.events.viewIdentityVerification, { initiator: initiator || '' });
    }
  }, [initiator, tabIndex]);

  // ================ DERIVED VALUES ====================
  const disableContinue =
    tabIndex === identityDialogIndexEnums.SUBMIT &&
    (!firstName ||
      !lastName ||
      !streetAddress ||
      !city ||
      !state ||
      !zipCode ||
      !dob ||
      !ssn ||
      !agreeToTerms ||
      Boolean(fieldErrors));

  let cancelText = 'Cancel';
  cancelText = tabIndex < identityDialogIndexEnums.LOADING ? 'Close' : cancelText;
  cancelText = tabIndex === identityDialogIndexEnums.SUCCESS ? 'Finish later' : cancelText;
  cancelText = tabIndex === identityDialogIndexEnums.ERROR ? 'Close' : cancelText;
  cancelText = tabIndex === identityDialogIndexEnums.LOCKOUT ? 'Close setup' : cancelText;

  let dialogTitle = 'Identity verification';
  dialogTitle = tabIndex === identityDialogIndexEnums.OPENING ? 'Set up account transfers' : dialogTitle;
  dialogTitle = tabIndex === identityDialogIndexEnums.SUBMIT ? 'Verify your identity' : dialogTitle;
  dialogTitle = tabIndex === identityDialogIndexEnums.PENDING ? 'Identity information submitted' : dialogTitle;
  dialogTitle = tabIndex === identityDialogIndexEnums.SUCCESS ? 'Identity verified' : dialogTitle;
  dialogTitle = tabIndex === identityDialogIndexEnums.ERROR ? 'Identity creation failed' : dialogTitle;
  dialogTitle = tabIndex === identityDialogIndexEnums.LOCKOUT ? 'Identity could not be verified' : dialogTitle;

  let continueText = 'Continue';
  if (tabIndex === identityDialogIndexEnums.OPENING) continueText = 'Get started';
  if (tabIndex === identityDialogIndexEnums.PENDING) continueText = 'Close setup';

  return (
    <StdDialog
      disableBackdropClick
      onClose={handleClose}
      open
      sharedcomponentid={'IDENTITY_VERIFICATION_DIALOG'}
      showCloseButton={mainView !== 'LOADING'}
      size={tabIndex === identityDialogIndexEnums.ERROR ? 'sm' : 'md'}
      title={dialogTitle}
    >
      <DialogContent>
        {tabIndex === identityDialogIndexEnums.OPENING && mainView === 'READY' && <SetupPage />}

        {tabIndex === identityDialogIndexEnums.SUBMIT && mainView === 'READY' && (
          <Box display='flex' flexDirection='column' alignItems='flex-start' width='100%'>
            {verificationStatus === 'FAILED' ? (
              <div className={classes.warningBox}>
                <WarningRoundedIcon className={classes.statusIcon} />
                <div className={classes.warningInner}>
                  <Typography variant='bodyMedium'>Unable to verify your identity</Typography>
                  <Typography variant='paragraphRegular'>
                    {isCurrentFailedAttempt
                      ? 'Double check your entries for any errors and try again. Enter your information as it appears on official documents like your driver’s license.'
                      : 'Try entering your information as it appears on official documents like your driver’s license.'}
                  </Typography>
                </div>
              </div>
            ) : (
              <Box alignItems='flex-start' marginBottom={2}>
                <Typography variant='bodyRegular'>
                  Complete the following information to verify your identity as the holder of your accounts.
                </Typography>
              </Box>
            )}
            <div className={classes.fieldRow}>
              <TextField
                value={firstName}
                onChange={firstNameChange}
                label='First name'
                className={classes.nameField}
                variant='filled'
                id='firstName-field'
                onBlur={handleTouchField}
                helperText=' ' // for spacing
              />
              <TextField
                value={lastName}
                onChange={lastNameChange}
                label='Last name'
                className={classes.nameField}
                variant='filled'
                id='lastName-field'
                onBlur={handleTouchField}
                helperText=' ' // for spacing
              />
            </div>
            <Typography color='textSecondary' component='span' variant='paragraphRegular'>
              Home address
              <InfoTooltipIcon
                tooltipClass={classes.tooltipLocation}
                className={classes.tooltipPlacementOne}
                tooltipPlacement='top'
                tooltipText={
                  "Enter the street address where you currently live. If you've relocated in the past 6 months, enter your previous address. Note that PO Boxes cannot be used."
                }
              />
            </Typography>
            <div className={classes.fieldRow}>
              <TextField
                value={streetAddress}
                onChange={addressChange}
                label='Street address'
                variant='filled'
                className={classes.addressField}
                error={fieldErrors?.streetAddress}
                helperText={fieldErrors?.streetAddress || ' '} // for spacing consistency
                id='streetAddress-field'
                onBlur={handleTouchField}
              />
              <TextField
                value={aptSuite}
                onChange={aptSuiteChange}
                label='Apt. / Suite #'
                variant='filled'
                className={classes.aptSuiteField}
                helperText=' ' // for spacing consistency
                id='aptSuite-field'
                onBlur={handleTouchField}
              />
            </div>
            <div className={classes.fieldRow}>
              <TextField
                value={city}
                onChange={cityChange}
                label='City'
                variant='filled'
                className={classes.cityField}
                helperText=' ' // for spacing consistency
                id='city-field'
                onBlur={handleTouchField}
              />
              <Autocomplete
                options={ALL_STATES}
                onChange={(_e, updatedValue) => setState(updatedValue)}
                className={classes.stateField}
                disableClearable
                autoHighlight
                autoSelect
                value={state?.length > 0 ? state : null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='State'
                    margin='normal'
                    variant='filled'
                    error={Boolean(fieldErrors?.state)}
                    helperText={fieldErrors?.state || ' '}
                    id='state-field'
                    onBlur={handleTouchField}
                  />
                )}
              />
              <TextField
                value={zipCode}
                onChange={zipChange}
                label='ZIP Code'
                variant='filled'
                className={classes.zipCodeField}
                error={Boolean(fieldErrors?.zipCode)}
                helperText={fieldErrors?.zipCode || ' '}
                id='zipCode-field'
                onBlur={handleTouchField}
                type='number'
              />
            </div>
            <Box display='flex' alignItems='flex-start' justifyContent='space-between' mt={2}>
              <Box display='flex' flexDirection='column' alignItems='flex-start' pr={4}>
                <Box
                  className={classes.fieldRow}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <DateField
                    date={dob}
                    onBlur={(val) => {
                      setDob(val);
                      setTouchedFields({ ...touchedFields, dob: true });
                    }}
                    className={classes.dateField}
                    allowInvalid
                    disableFuture
                    error={Boolean(fieldErrors?.dob)}
                    helperText={fieldErrors?.dob || ' '}
                    label='Date of birth'
                    id='date-field'
                  />
                  <Box display='flex' alignItems='center'>
                    <SSNField
                      format={'last4'}
                      value={ssn}
                      onChange={setSsn}
                      variant='filled'
                      error={Boolean(fieldErrors?.ssn)}
                      helperText={fieldErrors?.ssn || ' '}
                      className={classes.ssnField}
                      id='ssn-field'
                      onBlur={handleTouchField}
                    />
                    <InfoTooltipIcon
                      tooltipClass={classes.tooltipLocation}
                      className={classes.tooltipPlacementTwo}
                      tooltipPlacement='bottom'
                      tooltipText={
                        'The last four digits of your Social Security Number are used to verify your identity and may be utilized to collect additional identity-related information.'
                      }
                    />
                  </Box>
                </Box>
                {(verificationStatus === 'UNKNOWN' || !agreeToTerms) && (
                  <FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={agreeToTerms}
                          onChange={toggleAgreeToTerms}
                          className={classes.checkboxAlign}
                        />
                      }
                      label={
                        <Typography component='span' variant='paragraphRegular'>
                          I agree to the&nbsp;
                          <Typography
                            className={classes.termsLink}
                            component='span'
                            variant='inherit'
                            onClick={handleOpenTerms}
                          >
                            Terms of Service <OpenInNewIcon fontSize='inherit' />
                          </Typography>
                          &nbsp;and&nbsp;
                          <Typography
                            className={classes.termsLink}
                            component='span'
                            variant='inherit'
                            onClick={handleOpenPrivacyPolicy}
                          >
                            Privacy Policy <OpenInNewIcon fontSize='inherit' />
                          </Typography>
                          , as well as our partner&nbsp;
                          <Typography
                            className={classes.termsLink}
                            component='span'
                            variant='inherit'
                            onClick={handleOpenDwollaTerms}
                          >
                            Dwolla&apos;s Terms of Service <OpenInNewIcon fontSize='inherit' />
                          </Typography>
                          &nbsp;and&nbsp;
                          <Typography
                            className={classes.termsLink}
                            component='span'
                            variant='inherit'
                            onClick={handleOpenDwollaPrivacyPolicy}
                          >
                            Privacy Policy <OpenInNewIcon fontSize='inherit' />
                          </Typography>
                          , and consent to identity verification, &nbsp;which may include collecting additional
                          information &nbsp;about you from third parties.
                        </Typography>
                      }
                    />
                  </FormControl>
                )}
              </Box>
              <Box height='100%' display='flex' flexDirection='row'>
                <Divider variant='middle' orientation='vertical' flexItem />
                <SecurityPrivacyTrust />
              </Box>
            </Box>
          </Box>
        )}
        {tabIndex === identityDialogIndexEnums.PENDING && mainView === 'READY' && (
          <Box
            textAlign='center'
            pl={18}
            pr={18}
            pt={8}
            pb={8}
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
          >
            <QIllustration name='stopWatch' size='lg' />
            <br />
            <Typography variant='headlineRegular'>We&apos;re reviewing your identity</Typography>
            <br />
            <Typography color='textSecondary' variant='paragraphRegular'>
              Simplifi needs a little more time to review your information. We&apos;ll let you know once we’ve finished,
              so you can continue on to enable transfers!
            </Typography>
          </Box>
        )}

        {tabIndex === identityDialogIndexEnums.SUCCESS && mainView === 'READY' && isActiveIdentityValid && (
          <Box
            textAlign='center'
            pl={18}
            pr={18}
            pt={8}
            pb={8}
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
          >
            <QIllustration name='thumbsUp' size='lg' />
            <br />
            <Typography variant='headlineRegular'>Identity verified</Typography>
            <br />
            <Typography color='textSecondary' variant='paragraphRegular'>
              Next, choose which accounts to set up for transfers, and start moving money!
            </Typography>
          </Box>
        )}
        {tabIndex === identityDialogIndexEnums.LOCKOUT && mainView === 'ERROR' && (
          <Box
            textAlign='center'
            pl={12}
            pr={12}
            pt={8}
            pb={8}
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
          >
            <ZeroStateView
              title='Identity could not be verified'
              description='We’re sorry, but your identity could not be verified. You will not be able to transfer funds with Simplifi at this time. Additional verification options to enable transfers will be available in the future.'
              qIllustration='starSad'
            />
          </Box>
        )}
        {tabIndex === identityDialogIndexEnums.ERROR && mainView === 'ERROR' && (
          <Box className={classes.errorTab}>
            <ZeroStateView
              id='identity-failure'
              title={'Unable to create identity'}
              description={errorMessage}
              subDescription={errorCode && `Error Code: ${errorCode}`}
              qIllustration='planeCrash'
            />
          </Box>
        )}
        {mainView === 'LOADING' && (
          <Box
            paddingTop={20}
            paddingBottom={20}
            display='flex'
            alignItems='center'
            flexDirection='column'
            justifyContent='center'
          >
            <LoadingView size={100} />
          </Box>
        )}
        {mainView === 'FAILED' && (
          <Box className={classes.errorTab}>
            <ZeroStateView
              button={
                <QButton id={'try-again-button'} variant='outlined' onClick={refreshData}>
                  TRY AGAIN
                </QButton>
              }
              id='failed-to-fetch'
              title='Unable to fetch data'
              description="We can't fetch your data from the server. Please try again"
              qIllustration='planeCrash'
            />
          </Box>
        )}
        {mainView === 'NO_EMAIL' && (
          <Box className={classes.errorTab}>
            <ZeroStateView
              id='no-email-view'
              title='No verified email/phone'
              description='You have no verified email/phone on record. Please confirm your email/phone with us before continuing.'
              qIllustration='planeCrash'
            />
          </Box>
        )}
      </DialogContent>
      {(mainView === 'READY' || mainView === 'ERROR') && (
        <DialogActions>
          {(mainView === 'ERROR' || (mainView === 'READY' && tabIndex === identityDialogIndexEnums.SUCCESS)) && (
            <QButton
              variant='outlined'
              id='id-verification-cancel'
              aria-label='cancel'
              onClick={(e) => handleClose(e, 'CANCEL')}
            >
              {cancelText}
            </QButton>
          )}
          {mainView === 'READY' && (
            <QButton
              variant={tabIndex === identityDialogIndexEnums.PENDING ? 'outlined' : 'contained'}
              id='id-verification-continue'
              aria-label='continue'
              onClick={handleContinue}
              disabled={disableContinue}
            >
              {continueText}
            </QButton>
          )}
        </DialogActions>
      )}
    </StdDialog>
  );
}

IdentityVerificationDialog.propTypes = {
  initiator: PropTypes.string,
  startTabIndex: PropTypes.number,
};

export default IdentityVerificationDialog;
